import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStatsTooltip } from "./StatsTooltip";
import { TREND_METRICS, areaPath, bucketLabel, buildAxis, linePath, nearestIndex, xTickIndexes } from "./statsChart";
import { fmtDayShort } from "./statsFormat";

const HEIGHT = 320;
const MARGIN = { left: 70, right: 14, top: 12, bottom: 30 };
const CURRENT_COLOR = "var(--stats-current)";
const COMPARE_COLOR = "var(--stats-compare)";

// Width of the chart box; the SVG is drawn at that exact size so text never scales.
// Falls back to 600 where layout is not measurable (tests, first paint).
function useElementWidth(ref, fallback = 600) {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.floor(el.clientWidth);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * Line chart of one metric: this period (filled line) against the comparison period (grey line),
 * both on a single y axis so the lines are directly comparable.
 *
 * Reading it: hover/touch moves a crosshair with a tooltip for both periods; the plot is also a
 * keyboard stop, where Left/Right move the crosshair one point (a "Ver tabla" twin carries the same
 * numbers). Points without a value (a closed day has no occupancy) break the line instead of
 * dropping to zero.
 *
 * @param {{current: Array, comparison: Array|null, metric: "occ"|"rev"|"res", bucket: number,
 *   comparisonName: string, metrics?: object}} props  `current`/`comparison` come from `buildSeries`;
 *   `metrics` is the screen's metric table (label and formats, `TREND_METRICS` by default).
 */
export default function TrendChart({ current, comparison, metric, bucket, comparisonName, metrics = TREND_METRICS }) {
  const boxRef = useRef(null);
  const svgRef = useRef(null);
  const width = Math.max(280, useElementWidth(boxRef));
  const tooltip = useStatsTooltip();
  const [active, setActive] = useState(null);
  const lastIndex = useRef(current.length - 1);

  const { format, axis: formatAxis, label } = metrics[metric];
  const plotWidth = width - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const n = current.length;

  const values = [...current, ...(comparison || [])].map((p) => p.value).filter((v) => typeof v === "number");
  const { top, ticks } = buildAxis(metric, Math.max(0, ...values));
  const x = (i) => MARGIN.left + (n === 1 ? plotWidth / 2 : (i * plotWidth) / (n - 1));
  const y = (v) => MARGIN.top + (1 - v / top) * plotHeight;
  const baseline = y(0);

  // The tooltip belongs to the chart: leaving (unmount, metric or range change) must not strand it.
  useEffect(() => () => tooltip.hide(), [tooltip]);
  useEffect(() => {
    setActive(null);
    tooltip.hide();
    lastIndex.current = current.length - 1;
  }, [current, comparison, metric, tooltip]);

  const show = (index, clientX, clientY) => {
    const i = Math.max(0, Math.min(n - 1, index));
    lastIndex.current = i;
    setActive(i);
    const mine = current[i];
    const other = comparison ? comparison[i] : null;
    const rows = [{ value: format(mine.value), label: `Este período · ${bucketLabel(mine.date, bucket)}`, color: CURRENT_COLOR }];
    if (other) rows.push({ value: format(other.value), label: `${comparisonName} · ${bucketLabel(other.date, bucket)}`, color: COMPARE_COLOR });
    let px = clientX;
    let py = clientY;
    if (px === undefined && svgRef.current) {
      // Keyboard: anchor the tooltip to the point itself.
      const rect = svgRef.current.getBoundingClientRect();
      px = rect.left + (x(i) * rect.width) / width;
      py = rect.top + ((typeof mine.value === "number" ? y(mine.value) : baseline) * rect.height) / HEIGHT;
    }
    tooltip.show(null, rows, px || 0, py || 0);
  };

  const clear = () => {
    setActive(null);
    tooltip.hide();
  };

  const onPointerMove = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((event.clientX - rect.left) * width) / (rect.width || width);
    show(nearestIndex(px, MARGIN.left, plotWidth, n), event.clientX, event.clientY);
  };

  const onKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    show(lastIndex.current + (event.key === "ArrowRight" ? 1 : -1));
  };

  const xLabels = xTickIndexes(n, width < 480 ? 4 : 6);
  const mine = active !== null ? current[active] : null;
  const other = active !== null && comparison ? comparison[active] : null;

  return (
    <div className="stats-chart" ref={boxRef}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        role="group"
        aria-label={`${label}: este período comparado con ${comparisonName.toLowerCase() || "ningún período"}`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(tick)} y2={y(tick)} stroke={tick === 0 ? "var(--stats-axis)" : "var(--stats-grid)"} strokeWidth="1" />
            <text x={MARGIN.left - 10} y={y(tick) + 4} textAnchor="end" className="stats-axis-label">
              {formatAxis(tick)}
            </text>
          </g>
        ))}
        {xLabels.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={HEIGHT - 8}
            textAnchor={n === 1 ? "middle" : i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            className="stats-axis-label"
          >
            {fmtDayShort(current[i].date)}
          </text>
        ))}

        {comparison && (
          <path d={linePath(comparison.slice(0, n), x, y)} fill="none" stroke={COMPARE_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {n > 1 && <path d={areaPath(current, x, y, baseline)} fill="var(--stats-current-wash)" />}
        <path d={linePath(current, x, y)} fill="none" stroke={CURRENT_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {typeof current[n - 1].value === "number" && (
          <circle cx={x(n - 1)} cy={y(current[n - 1].value)} r="4" fill={CURRENT_COLOR} stroke="var(--stats-bg)" strokeWidth="2" />
        )}

        {mine && (
          <g aria-hidden="true">
            <line x1={x(active)} x2={x(active)} y1={MARGIN.top} y2={HEIGHT - MARGIN.bottom} stroke="var(--stats-ink)" strokeWidth="1" />
            {other && typeof other.value === "number" && (
              <circle cx={x(active)} cy={y(other.value)} r="4.5" fill={COMPARE_COLOR} stroke="var(--stats-bg)" strokeWidth="2" />
            )}
            {typeof mine.value === "number" && (
              <circle cx={x(active)} cy={y(mine.value)} r="4.5" fill={CURRENT_COLOR} stroke="var(--stats-bg)" strokeWidth="2" />
            )}
          </g>
        )}

        <rect
          className="stats-plot-hit"
          x={MARGIN.left - 6}
          y={MARGIN.top}
          width={plotWidth + 12}
          height={plotHeight}
          fill="transparent"
          tabIndex={0}
          aria-label="Usá las flechas para recorrer los valores"
          onPointerMove={onPointerMove}
          onPointerLeave={clear}
          onFocus={() => show(lastIndex.current)}
          onBlur={clear}
          onKeyDown={onKeyDown}
        />
      </svg>
    </div>
  );
}
